<script setup lang="ts">
import MinusIcon from '@/assets/icons/MinusIcon.vue';
import PlusIcon from '@/assets/icons/PlusIcon.vue';
import { EKeyboardKey } from '@/utils/menu.utils';
import { clampNumber } from 'common/utils/math.utils';
import { ceilToStep, getStepAlignedMaximum, roundToNearestStep, toNumberOrNull } from '@/utils/number.utils';
import { computed, onBeforeUnmount, onMounted, ref, toRefs, useId, useTemplateRef, watch } from 'vue';

type NumberStepperVariant = 'default' | 'compact' | 'large' | 'plain';

const inputDomId = useId();

const emit = defineEmits<{
  'update:modelValue': [value: number];
}>();

const props = withDefaults(
  defineProps<{
    modelValue: number;
    label?: string;
    disabled?: boolean;
    placeholder?: string;
    maxlength?: number;
    hasErrors?: boolean;
    step?: number;
    min?: number;
    max?: number;
    rounded?: boolean;
    ceilStep?: boolean;
    unit?: string;
    variant?: NumberStepperVariant;
    decreaseLabel?: string;
    increaseLabel?: string;
    ariaLabel?: string;
  }>(),
  {
    ariaLabel: undefined,
    decreaseLabel: 'Decrease',
    disabled: false,
    hasErrors: false,
    increaseLabel: 'Increase',
    label: undefined,
    maxlength: undefined,
    max: undefined,
    min: undefined,
    placeholder: undefined,
    rounded: false,
    ceilStep: false,
    step: 1,
    unit: undefined,
    variant: 'default',
  },
);

const { modelValue, label, maxlength, hasErrors, disabled } = toRefs(props);

const inputValue = ref<string>('');
const isFocused = ref(false);
const inputElRef = useTemplateRef<HTMLInputElement>('inputElRef');
let skipNextBlurCommit = false;

const minValue = computed<number>(() => props.min ?? Number.NEGATIVE_INFINITY);
const maxValue = computed<number>(() => props.max ?? Number.POSITIVE_INFINITY);
const stepBase = computed<number>(() => (props.min != null && Number.isFinite(props.min) ? props.min : 0));
const stepAlignedMaxValue = computed<number>(() => getStepAlignedMaximum(maxValue.value, props.step, stepBase.value));
const ariaMaxValue = computed<number | undefined>(() =>
  Number.isFinite(stepAlignedMaxValue.value) ? stepAlignedMaxValue.value : undefined,
);
const numericInputValue = computed<number | null>(() => toNumberOrNull(inputValue.value));
const currentValue = computed<number>(() => normalizeValue(numericInputValue.value ?? modelValue.value));
const decreaseDisabled = computed<boolean>(() => disabled.value || currentValue.value <= minValue.value);
const increaseDisabled = computed<boolean>(() => disabled.value || currentValue.value >= stepAlignedMaxValue.value);

const normalizeValue = (value: number): number => {
  const stepRoundedValue = props.ceilStep
    ? ceilToStep(value, props.step, stepBase.value)
    : roundToNearestStep(value, props.step, stepBase.value);
  return clampNumber(stepRoundedValue, minValue.value, stepAlignedMaxValue.value);
};

const isInputActive = (): boolean =>
  isFocused.value || (typeof document !== 'undefined' && document.activeElement === inputElRef.value);

const sanitizeInputString = (s: string): string => {
  return s.replace(/\D/g, '');
};

const syncInputValueFromModel = (): void => {
  inputValue.value = String(normalizeValue(modelValue.value));
};

const emitValue = (value: number): void => {
  emit('update:modelValue', normalizeValue(value));
};

const handleInput = (event: Event): void => {
  isFocused.value = true;
  const target = event.target as HTMLInputElement;
  const raw = target.value;
  const cleaned = sanitizeInputString(raw);

  inputValue.value = cleaned;
  if (target.value !== cleaned) target.value = cleaned;

  const nextValue = toNumberOrNull(cleaned);
  if (nextValue == null) return;
  emitValue(nextValue);
};

const commitNow = (): void => {
  const fallback = Number.isFinite(minValue.value) ? minValue.value : modelValue.value;
  const nextValue = normalizeValue(numericInputValue.value ?? fallback);
  inputValue.value = String(nextValue);
  emitValue(nextValue);
};

const handleEnter = (): void => {
  skipNextBlurCommit = true;
  commitNow();
  inputElRef.value?.blur();
  setTimeout(() => {
    skipNextBlurCommit = false;
  }, 0);
};

const handleBlur = (): void => {
  isFocused.value = false;
  if (skipNextBlurCommit) {
    skipNextBlurCommit = false;
    return;
  }
  commitNow();
};

const stepBy = (delta: number): void => {
  if (disabled.value) return;

  const current = numericInputValue.value ?? modelValue.value;
  const next = normalizeValue(current + delta);
  inputValue.value = String(next);
  emitValue(next);
};

const inc = (): void => stepBy(props.step);
const dec = (): void => stepBy(-props.step);

let holdTimer: number | null = null;
let holdInterval: number | null = null;

const startHold = (fn: () => void, isActionDisabled: boolean): void => {
  if (isActionDisabled) return;
  fn();
  holdTimer = window.setTimeout(() => {
    holdInterval = window.setInterval(fn, 60);
  }, 350);
};
const stopHold = (): void => {
  if (holdTimer != null) {
    clearTimeout(holdTimer);
    holdTimer = null;
  }
  if (holdInterval != null) {
    clearInterval(holdInterval);
    holdInterval = null;
  }
};

const onKeydown = (e: KeyboardEvent): void => {
  if (disabled.value) return;
  if (e.key === EKeyboardKey.Up) {
    e.preventDefault();
    inc();
  } else if (e.key === EKeyboardKey.Down) {
    e.preventDefault();
    dec();
  } else if (e.key === EKeyboardKey.PageUp) {
    e.preventDefault();
    stepBy(props.step * 10);
  } else if (e.key === EKeyboardKey.PageDown) {
    e.preventDefault();
    stepBy(-props.step * 10);
  } else if (e.key === EKeyboardKey.Enter) {
    e.preventDefault();
    handleEnter();
  }
};

watch(
  modelValue,
  () => {
    if (!isInputActive()) syncInputValueFromModel();
  },
  { immediate: true },
);

onMounted(() => {
  window.addEventListener('mouseup', stopHold);
  window.addEventListener('mouseleave', stopHold);
  window.addEventListener('touchend', stopHold);
});
onBeforeUnmount(() => {
  stopHold();
  window.removeEventListener('mouseup', stopHold);
  window.removeEventListener('mouseleave', stopHold);
  window.removeEventListener('touchend', stopHold);
});

const focus = (): void => inputElRef.value?.focus();
const select = (): void => inputElRef.value?.select();
defineExpose({ focus, select });
</script>

<template>
  <div class="relative">
    <div
      v-if="variant === 'plain'"
      :class="[
        'relative flex min-h-button min-w-0 items-center overflow-hidden rounded-input border border-quaternary bg-surface-primary pl-smd pr-md py-sm',
        'focus-within:border-2 focus-within:border-tint',
        hasErrors && 'outline-inner-error',
        disabled && 'opacity-60 cursor-not-allowed',
      ]"
    >
      <input
        :id="inputDomId"
        ref="inputElRef"
        class="h-[27px] min-h-0 w-full border-0 bg-transparent p-0 text-body leading-[1.7] text-primary outline-none caret-tint selection:bg-tint/10 selection:text-primary"
        aria-live="polite"
        role="spinbutton"
        :aria-label="props.ariaLabel"
        :aria-valuemin="props.min ?? undefined"
        :aria-valuemax="ariaMaxValue"
        :aria-valuenow="numericInputValue ?? modelValue"
        inputmode="numeric"
        autocomplete="off"
        :maxlength="maxlength"
        :placeholder="placeholder"
        :disabled="disabled"
        :value="inputValue"
        @input="handleInput"
        @focus="isFocused = true"
        @blur="handleBlur"
        @keydown="onKeydown"
      />
    </div>

    <div
      v-else-if="variant !== 'compact'"
      :class="[
        'flex items-center gap-sm rounded-sm border bg-white px-md py-sm',
        props.variant === 'large' && 'min-h-list-item-lg rounded-md',
        disabled && 'opacity-60 cursor-not-allowed',
        hasErrors && 'outline-inner-error',
        rounded && 'rounded-dialog!',
      ]"
    >
      <div class="flex flex-1 flex-col justify-center">
        <label
          v-if="label"
          :for="inputDomId"
          class="text-footer text-muted transition-all duration-200 pointer-events-none"
        >
          {{ label }}
        </label>
        <input
          :id="inputDomId"
          ref="inputElRef"
          class="input-text h-full flex-1 bg-transparent outline-none border-0 !p-0 text-title max-h-icon-lg min-h-0 focus:outline-none"
          aria-live="polite"
          role="spinbutton"
          :aria-label="!label ? props.ariaLabel : undefined"
          :aria-valuemin="props.min ?? undefined"
          :aria-valuemax="ariaMaxValue"
          :aria-valuenow="numericInputValue ?? modelValue"
          inputmode="numeric"
          autocomplete="off"
          :maxlength="maxlength"
          :placeholder="!label ? placeholder : ''"
          :disabled="disabled"
          :value="inputValue"
          @input="handleInput"
          @focus="isFocused = true"
          @blur="handleBlur"
          @keydown="onKeydown"
        />
      </div>
      <button
        type="button"
        class="size-icon-lg shrink-0 rounded-full flex items-center justify-center bg-surface-secondary"
        :class="[decreaseDisabled ? 'cursor-not-allowed opacity-50' : 'hover:shadow-sm active:scale-95']"
        :disabled="decreaseDisabled"
        :aria-label="decreaseLabel"
        @mousedown.prevent="startHold(dec, decreaseDisabled)"
        @touchstart.prevent="startHold(dec, decreaseDisabled)"
        @mouseup="stopHold"
        @mouseleave="stopHold"
        @keydown.enter.prevent="dec"
        @keydown.space.prevent="dec"
      >
        <MinusIcon class="size-icon-sm" />
      </button>
      <button
        type="button"
        class="size-icon-lg shrink-0 rounded-full flex items-center justify-center bg-surface-secondary"
        :class="[increaseDisabled ? 'cursor-not-allowed opacity-50' : 'hover:shadow-sm active:scale-95']"
        :disabled="increaseDisabled"
        :aria-label="increaseLabel"
        @mousedown.prevent="startHold(inc, increaseDisabled)"
        @touchstart.prevent="startHold(inc, increaseDisabled)"
        @mouseup="stopHold"
        @mouseleave="stopHold"
        @keydown.enter.prevent="inc"
        @keydown.space.prevent="inc"
      >
        <PlusIcon class="size-icon-sm" />
      </button>
    </div>

    <div v-else class="flex w-full items-center gap-sm">
      <div
        :class="[
          'relative flex min-h-button min-w-0 flex-1 items-center justify-center overflow-hidden rounded-input border border-quaternary bg-surface-primary',
          'focus-within:border-2 focus-within:border-tint',
          hasErrors && 'outline-inner-error',
          disabled && 'opacity-60 cursor-not-allowed',
        ]"
      >
        <button
          type="button"
          class="absolute left-sm top-1/2 z-[1] flex size-icon-button -translate-y-1/2 items-center justify-center rounded-full bg-surface-tertiary transition duration-300"
          :class="[decreaseDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-surface-divider active:scale-95']"
          :disabled="decreaseDisabled"
          :aria-label="decreaseLabel"
          @mousedown.prevent="startHold(dec, decreaseDisabled)"
          @touchstart.prevent="startHold(dec, decreaseDisabled)"
          @mouseup="stopHold"
          @mouseleave="stopHold"
          @keydown.enter.prevent="dec"
          @keydown.space.prevent="dec"
        >
          <MinusIcon class="size-icon-sm" />
        </button>

        <input
          :id="inputDomId"
          ref="inputElRef"
          class="min-h-button w-full border-0 bg-transparent px-button py-sm text-center text-body leading-[1.7] text-primary outline-none caret-tint selection:bg-tint/10 selection:text-primary"
          aria-live="polite"
          role="spinbutton"
          :aria-valuemin="props.min ?? undefined"
          :aria-valuemax="ariaMaxValue"
          :aria-valuenow="numericInputValue ?? modelValue"
          inputmode="numeric"
          autocomplete="off"
          :maxlength="maxlength"
          :placeholder="placeholder"
          :disabled="disabled"
          :value="inputValue"
          @input="handleInput"
          @focus="isFocused = true"
          @blur="handleBlur"
          @keydown="onKeydown"
        />

        <button
          type="button"
          class="absolute right-smd top-1/2 z-[1] flex size-icon-button -translate-y-1/2 items-center justify-center rounded-full bg-surface-tertiary transition duration-300"
          :class="[increaseDisabled ? 'cursor-not-allowed opacity-50' : 'hover:bg-surface-divider active:scale-95']"
          :disabled="increaseDisabled"
          :aria-label="increaseLabel"
          @mousedown.prevent="startHold(inc, increaseDisabled)"
          @touchstart.prevent="startHold(inc, increaseDisabled)"
          @mouseup="stopHold"
          @mouseleave="stopHold"
          @keydown.enter.prevent="inc"
          @keydown.space.prevent="inc"
        >
          <PlusIcon class="size-icon-sm" />
        </button>
      </div>

      <span v-if="unit" class="text-body leading-[1.7] text-secondary">{{ unit }}</span>
    </div>
  </div>
</template>
